import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { TextField } from "@mui/material";
import { NavigateBefore } from "@mui/icons-material";
import PublishIcon from "@mui/icons-material/Publish";

import AppDialog from "../../../components/AppDialog/AppDialog";
import CustomButton from "../../../components/buttons/CustomButton";
import LanguageTabs from "../../../components/LanguageTab/LanguageTabs";
import ArrowIcon from "../../../utils/ArrowIcon";
import { availableLanguages } from "../../../i18n";
import { toggleSection, validateURL } from "../../../utils/constants";
import {
  isValidHttpUrl,
  joinTruthyList,
  normalizeLangArray,
  normalizeTrimmedLangEntries,
  parseCommaSeparatedList,
} from "../../../utils/formUtils";

import "./CreateDistribution.css";
import DropDownType from "../../../components/buttons/DropDownType";
import DropDownStatus from "../../../components/buttons/DropDownOfferStatus";
import DataServiceTable from "../../PrivateArea/DataService/DataServiceTable";
import DropDownRights from "../../../components/buttons/DropDownRights";
import DropDownPolicies from "../../../components/buttons/DropDownPolicies";
import DropDownVocabulary from "../../../components/buttons/DropDownVocabulary";

const canonicalizePayload = (payload) => {
  const title = normalizeTrimmedLangEntries(payload.title || []);
  const description = normalizeTrimmedLangEntries(payload.description || []);

  const applicable_legislation = Array.isArray(payload.applicable_legislation)
    ? [...payload.applicable_legislation]
        .map((x) => String(x).trim())
        .filter(Boolean)
        .sort()
    : [];

  const has_policies = Array.isArray(payload.has_policies)
    ? [...payload.has_policies]
        .map((x) => String(x).trim())
        .filter(Boolean)
        .sort()
    : [];

  const out = {
    ...payload,
    title,
    description,
    access_service: String(payload.access_service || "").trim(),
    access_url: String(payload.access_url || "").trim(),
    conforms_to: String(payload.conforms_to || "").trim(),
    format: String(payload.format || "").trim(),
    status: String(payload.status || "").trim(),
    applicable_legislation,
    has_policies,
  };

  if (out.byte_size === "" || out.byte_size == null) delete out.byte_size;

  return out;
};

const buildPayloadFromForm = (formState) => {
  const title = (formState.title || [])
    .map((x) => ({ language: x.language, value: String(x.value || "").trim() }))
    .filter((x) => x.value.length > 0);

  const description = (formState.description || [])
    .map((x) => ({ language: x.language, value: String(x.value || "").trim() }))
    .filter((x) => x.value.length > 0);

  const applicable_legislation = parseCommaSeparatedList(
    formState.applicable_legislation,
  );

  const byteSizeParsed =
    formState.byte_size === "" || formState.byte_size == null
      ? undefined
      : Number(formState.byte_size);

  const has_policies = Array.isArray(formState.has_policies)
    ? formState.has_policies.filter(Boolean)
    : [];

  const payload = {
    access_service: String(formState.access_service || "").trim(),
    access_url: String(formState.access_url || "").trim(),
    applicable_legislation,
    byte_size: Number.isFinite(byteSizeParsed) ? byteSizeParsed : undefined,
    conforms_to: String(formState.conforms_to || "").trim(),
    description,
    rights: String(formState.rights || "").trim(),
    format: String(formState.format || "").trim(),
    has_policies,
    status: String(formState.status || "").trim(),
    title,
  };

  if (payload.byte_size === undefined) delete payload.byte_size;

  return payload;
};

const emptyForm = (baseLang) => ({
  title: [{ language: baseLang, value: "" }],
  description: [{ language: baseLang, value: "" }],
  format: "",
  status: "",
  access_service: "",
  conforms_to: "",
  access_url: "",
  applicable_legislation: "",
  byte_size: 123456,
  has_policies: [],
  rights: "",
});

export default function DistributionForm({
  open,
  onClose,
  onSubmit,
  loading = false,
  t,
  i18n,
  initialDistribution = null,
  includeIdentifiersInPayload = true,
  enableDirtyCheck = true,
  formatOptions,
  statusOptions,
  rightsOptions,
  selectedPolicyIris,
  selectedPolicies,
  vocabOptions,
}) {
  const [baseLang] = useState(() => i18n.language);
  const mode = initialDistribution ? "edit" : "create";
  const languages = availableLanguages;

  const [activeLang, setActiveLang] = useState(baseLang);
  const [form, setForm] = useState(() => emptyForm(baseLang));
  const initialCanonicalRef = useRef(null);
  const [isDataServiceOpen, setIsDataServiceOpen] = useState(true);

  const policyOptions = useMemo(() => {
    return (selectedPolicies || [])
      .map((p) => {
        const iri = p?.id;
        if (!iri) return null;

        const title = p?.title?.trim() || "—";
        const rc = p?.ruleCounts || {};
        const meta = {
          permissions: rc.permissions ?? 0,
          obligations: rc.obligations ?? 0,
          prohibitions: rc.prohibitions ?? 0,
          assignerShort: p?.assignerShort,
        };

        return { label: title, value: iri, meta };
      })
      .filter(Boolean);
  }, [selectedPolicies]);

  const vocabLabels = useMemo(
    () => (vocabOptions ?? []).map((o) => o.label),
    [vocabOptions],
  );

  const vocabValues = useMemo(
    () => (vocabOptions ?? []).map((o) => o.value),
    [vocabOptions],
  );

  const vocabVersions = useMemo(
    () => (vocabOptions ?? []).map((o) => o.version),
    [vocabOptions],
  );

  const hydrate = useCallback(() => {
    if (mode === "create" || !initialDistribution) {
      setActiveLang(baseLang);
      const next = emptyForm(baseLang);
      setForm(next);
      initialCanonicalRef.current = canonicalizePayload(
        buildPayloadFromForm(next),
      );
      return;
    }

    const d = initialDistribution;

    const title = normalizeLangArray(d.title, baseLang);
    const description = normalizeLangArray(d.description, baseLang);

    const langsPresent = title.map((x) => x.language);
    const initialActive = langsPresent.includes(baseLang)
      ? baseLang
      : title[0]?.language || baseLang;

    const next = {
      title,
      description,

      format: d.format || "",
      status: d.status || "",
      access_service: d.access_service || "",

      conforms_to: d.conforms_to || "",
      access_url: d.access_url || "",

      applicable_legislation: joinTruthyList(d.applicable_legislation),
      byte_size: d.byte_size ?? "",

      rights: d.rights || "",
      has_policies: Array.isArray(d.has_policies) ? d.has_policies : [],
    };

    setActiveLang(initialActive);
    setForm(next);

    initialCanonicalRef.current = canonicalizePayload(
      buildPayloadFromForm(next),
    );
  }, [mode, initialDistribution, baseLang]);

  useEffect(() => {
    if (!open) return;
    hydrate();
  }, [open, hydrate]);

  useEffect(() => {
    if (!open) return;

    setForm((prev) => {
      const hasTitle = prev.title.some((x) => x.language === activeLang);
      const hasDesc = prev.description.some((x) => x.language === activeLang);
      if (hasTitle && hasDesc) return prev;

      return {
        ...prev,
        title: hasTitle
          ? prev.title
          : [...prev.title, { language: activeLang, value: "" }],
        description: hasDesc
          ? prev.description
          : [...prev.description, { language: activeLang, value: "" }],
      };
    });
  }, [activeLang, open]);

  const getLangValue = useCallback((arr, language) => {
    return arr.find((x) => x.language === language)?.value ?? "";
  }, []);

  const setLangValue = useCallback(
    (key, language) => (e) => {
      const v = e.target.value;
      setForm((prev) => ({
        ...prev,
        [key]: prev[key].map((item) =>
          item.language === language ? { ...item, value: v } : item,
        ),
      }));
    },
    [],
  );

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const addLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code) return;

    setForm((prev) => {
      const hasTitle = prev.title.some((x) => x.language === code);
      const hasDesc = prev.description.some((x) => x.language === code);
      if (hasTitle && hasDesc) return prev;

      return {
        ...prev,
        title: hasTitle
          ? prev.title
          : [...prev.title, { language: code, value: "" }],
        description: hasDesc
          ? prev.description
          : [...prev.description, { language: code, value: "" }],
      };
    });

    if (code !== activeLang) setActiveLang(code);
  };

  const removeLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code) return;

    setForm((prev) => {
      const nextTitle = prev.title.filter((x) => x.language !== code);
      const nextDesc = prev.description.filter((x) => x.language !== code);
      if (nextTitle.length === 0 || nextDesc.length === 0) return prev;
      return { ...prev, title: nextTitle, description: nextDesc };
    });

    if (activeLang === code) {
      const next = form.title.map((x) => x.language).find((l) => l !== code);
      if (next) setActiveLang(next);
    }
  };

  const activeTitle = getLangValue(form.title, activeLang);
  const activeDescription = getLangValue(form.description, activeLang);

  const accessUrlOk = useMemo(
    () => isValidHttpUrl(form.access_url),
    [form.access_url],
  );

  const applicable_legislation = useMemo(() => {
    return parseCommaSeparatedList(form.applicable_legislation);
  }, [form.applicable_legislation]);

  const legislationError = useMemo(() => {
    if (applicable_legislation.length === 0) return "";
    for (const entry of applicable_legislation) {
      const { valid, error } = validateURL(entry, t);
      if (!valid) return error || (t ? t("common.invalid_url") : "URL inválida");
    }
    return "";
  }, [applicable_legislation, t]);

  const hasAnyLangText = useMemo(() => {
    const anyTitle = form.title.some((x) => x.value.trim().length > 0);
    const anyDesc = form.description.some((x) => x.value.trim().length > 0);
    return anyTitle && anyDesc;
  }, [form.title, form.description]);

  const byteSizeOk = useMemo(() => {
    if (form.byte_size === "" || form.byte_size == null) return true;
    const n = Number(form.byte_size);
    return Number.isFinite(n) && n >= 0;
  }, [form.byte_size]);

  const canSubmit = useMemo(() => {
    const formatOk = String(form.format || "").trim().length > 0;
    const statusOk = String(form.status || "").trim().length > 0;
    const rightsOk = String(form.rights || "").trim().length > 0;
    const accessUrlRequiredOk =
      String(form.access_url || "").trim().length > 0 && accessUrlOk;

    const legislationOk =
      applicable_legislation.length === 0 || legislationError === "";

    return (
      hasAnyLangText &&
      formatOk &&
      statusOk &&
      rightsOk &&
      accessUrlRequiredOk &&
      legislationOk &&
      byteSizeOk &&
      !loading
    );
  }, [
    hasAnyLangText,
    form.format,
    form.status,
    form.rights,
    form.access_url,
    accessUrlOk,
    applicable_legislation.length,
    legislationError,
    byteSizeOk,
    loading,
  ]);

  const currentPayload = useMemo(() => buildPayloadFromForm(form), [form]);

  const isDirty = useMemo(() => {
    if (mode !== "edit" || !enableDirtyCheck) return true;
    if (!initialCanonicalRef.current) return true;
    const now = canonicalizePayload(currentPayload);
    return JSON.stringify(now) !== JSON.stringify(initialCanonicalRef.current);
  }, [mode, enableDirtyCheck, currentPayload]);

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;

    const payload = { ...currentPayload };

    if (mode === "edit" && includeIdentifiersInPayload && initialDistribution) {
      if (initialDistribution.id) payload.id = initialDistribution.id;
      if (initialDistribution.identifier)
        payload.identifier = initialDistribution.identifier;
    }

    await onSubmit?.(payload, { mode, initialDistribution });
  };

  const usedLangs = form.title.map((x) => x.language);
  const availableLangs = languages.filter((l) => !usedLangs.includes(l.code));

  const dialogTitle =
    mode === "edit"
      ? t
        ? t("wizard.steps.dataset.distribution.updateDistribution")
        : "Editar distribución"
      : t
        ? t("wizard.steps.dataset.distribution.createDistribution")
        : "Crear distribución";

  const submitLabel =
    mode === "edit"
      ? t
        ? t("common.save")
        : "Guardar"
      : t
        ? t("common.create")
        : "Crear";

  const pickValue = (eOrValue) => {
    if (eOrValue && eOrValue.target) return eOrValue.target.value;
    return eOrValue;
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth="lg"
      classes={{
        paper: "distributionDialogPaper",
        content: "distributionDialogContent",
      }}
      disableBackdropClose={loading}
      disableEscapeClose={loading}
      actions={
        <>
          <CustomButton
            onClick={onClose}
            disabled={loading}
            startIcon={<NavigateBefore />}
          >
            {t ? t("common.cancel") : "Cancelar"}
          </CustomButton>

          <CustomButton
            onClick={handleSubmit}
            disabled={!canSubmit || loading || (mode === "edit" && !isDirty)}
            variant="contained"
            startIcon={<PublishIcon />}
          >
            {submitLabel}
          </CustomButton>
        </>
      }
    >
      <LanguageTabs
        usedLangs={usedLangs}
        activeLang={activeLang}
        onChangeActiveLang={(lang) => {
          const code = typeof lang === "string" ? lang : lang?.code;
          if (code && code !== activeLang) setActiveLang(code);
        }}
        onAddLang={addLanguage}
        onRemoveLang={removeLanguage}
        languages={availableLangs}
        addLabel={t ? t("common.addLanguage") : "Añadir idioma"}
        disabled={loading}
      />

      <div className="dist-grid">
        <div className="dist-left">
          <TextField
            label={
              t
                ? t("wizard.steps.dataset.distribution.name")
                : "Nombre de distribución"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.distribution.name_placeholder")
                : "Escribe el nombre de la distribución"
            }
            value={activeTitle}
            onChange={setLangValue("title", activeLang)}
            fullWidth
            required
          />

          <TextField
            label={
              t
                ? t("wizard.steps.dataset.distribution.description")
                : "Descripción"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.distribution.description_placeholder")
                : "Escribe la descripción de la distribución"
            }
            value={activeDescription}
            onChange={setLangValue("description", activeLang)}
            fullWidth
            multiline
            minRows={4}
            required
          />

          <TextField
            label={
              t
                ? t("wizard.steps.dataset.distribution.legislation")
                : "Legislación aplicable"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.distribution.legislation_placeholder")
                : "Escribe la legislación que aplica a la distribución (separada por comas)"
            }
            value={form.applicable_legislation}
            onChange={setField("applicable_legislation")}
            fullWidth
            multiline
            minRows={4}
            error={form.applicable_legislation.trim().length > 0 && legislationError !== ""}
            helperText={
              form.applicable_legislation.trim().length === 0
                ? " "
                : legislationError || " "
            }
          />
        </div>

        <div className="dist-right">
          <div className="dist-row2">
            <div className="dist-field">
              <p className="ptitleinput">
                {t ? t("wizard.steps.dataset.distribution.format") : "Formato"}
                <span className="required">*</span>
              </p>
              <DropDownType
                value={form.format}
                onChange={(eOrValue) =>
                  setForm((prev) => ({ ...prev, format: pickValue(eOrValue) }))
                }
                options={formatOptions.map((item) => item.label)}
                optionsValue={formatOptions.map((item) => item.concept_iri)}
                disabled={loading}
              />
            </div>
            <div className="dist-field">
              <p className="ptitleinput">
                {t ? t("wizard.steps.dataset.distribution.status") : "Estado"}
                <span className="required">*</span>
              </p>
              <DropDownStatus
                value={form.status}
                onChange={(eOrValue) =>
                  setForm((prev) => ({ ...prev, status: pickValue(eOrValue) }))
                }
                options={statusOptions.map((item) => item.label)}
                optionsValue={statusOptions.map((item) => item.concept_iri)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="dist-row2">
            <DropDownPolicies
              value={form.has_policies}
              onChange={(next) =>
                setForm((prev) => ({
                  ...prev,
                  has_policies: Array.isArray(next) ? next : [],
                }))
              }
              policies={policyOptions}
              disabled={loading}
            />
            <div className="dist-field">
              <p className="ptitleinput">
                {t ? t("wizard.steps.dataset.rights") : "Derechos"}
                <span className="required">*</span>
              </p>
              <DropDownRights
                value={form.rights}
                onChange={(eOrValue) =>
                  setForm((prev) => ({ ...prev, rights: pickValue(eOrValue) }))
                }
                options={rightsOptions.map((item) => item.label)}
                optionsValue={rightsOptions.map((item) => item.concept_iri)}
                disabled={loading}
              />
            </div>
          </div>

          <TextField
            label={
              t
                ? t("wizard.steps.dataset.distribution.accessUrl")
                : "URL de acceso"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.distribution.accessUrl_placeholder")
                : "Escribe la URL de acceso de la distribución"
            }
            value={form.access_url}
            onChange={setField("access_url")}
            required
            error={String(form.access_url || "").trim().length > 0 && !accessUrlOk}
            helperText={
              String(form.access_url || "").trim().length > 0 && !accessUrlOk
                ? t
                  ? t("common.invalid_url")
                  : "URL inválida"
                : " "
            }
            fullWidth
          />
          <DropDownVocabulary
            value={form.conforms_to}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, conforms_to: e.target.value }))
            }
            options={vocabLabels}
            optionsValue={vocabValues}
            optionsVersion={vocabVersions}
            disabled={loading}
          />
        </div>
      </div>
      {/* DataService */}
      <div
        className="createdivplatform w100"
        onClick={() =>
          toggleSection(setIsDataServiceOpen, ".createdivplatform")
        }
        style={{ cursor: "pointer" }}
      >
        <h4 className="ptb3lr4">
          {t("wizard.steps.dataset.dataservice.title")}
        </h4>
        <ArrowIcon open={isDataServiceOpen} />
      </div>
      {isDataServiceOpen && (
        <div className="ptb2 dist-dataservice-wrap">
          <DataServiceTable
            variant="embedded"
            showBreadcrumb={false}
            showHeader={true}
            selectionMode="single"
            onSelectionChange={(ids) => {
              const id = ids[0] ?? "";
              setForm((prev) => ({ ...prev, access_service: id }));
            }}
          />
        </div>
      )}
    </AppDialog>
  );
}
